/**
 * Gainsight PX custom-event tracking (design doc 0026). The SDK itself is loaded by the app
 * (`apps/web/src/gainsight.ts`); this module only talks to the `window.aptrinsic` global, so the
 * UI package stays free of any loader or environment knowledge.
 */

export type AnalyticsProps = Record<string, string | number | boolean>;

/** The Gainsight PX command function installed on `window` by the bootstrap snippet. */
export type Aptrinsic = (command: string, ...args: unknown[]) => void;

declare global {
  interface Window {
    aptrinsic?: Aptrinsic;
  }
}

/** Event names, sentence case with the feature prefix used across TigerConnect frontends. */
export const ANALYTICS_EVENTS = {
  newSession: "Ask Tiger - New Session",
  messageSent: "Ask Tiger - Message Sent",
  genuiBlockRendered: "Ask Tiger - GenUI Block Rendered",
  suggestionChipClicked: "Ask Tiger - Suggestion Chip Clicked",
  feedbackGiven: "Ask Tiger - Feedback Given",
} as const;

/**
 * Track a custom event. No-ops when the SDK isn't installed (tests, blocked script); once the
 * bootstrap stub exists, calls made before the script finishes loading are queued by it.
 * Never throws — analytics must not break the chat.
 */
export function trackEvent(name: string, props: AnalyticsProps = {}): void {
  const aptrinsic = typeof window === "undefined" ? undefined : window.aptrinsic;
  if (!aptrinsic) return;
  try {
    aptrinsic("track", name, props);
  } catch (err) {
    console.error("[tiger-agent] Gainsight tracking failed", err);
  }
}
